<template>
  <div ref="flyout" class="p-4" :class="{ 'flex w-full flex-col': isMobile }">
    <SFHeadline
      v-if="!isMobile"
      class="w-full justify-center pb-5 text-center font-bold"
      data-testid="headline"
      font-family="geist"
      is-bold
      size="xl"
      tag="h4"
    >
      {{ $t('basket_page.title') }}
    </SFHeadline>

    <div
      v-else
      class="z-50 flex w-full items-center justify-between bg-white pb-4"
    >
      <IconLogo class="h-5" />
      <div>
        <SFHeadline
          class="w-full justify-center text-center font-bold"
          data-testid="headline"
          font-family="geist"
          is-bold
          size="lg"
          tag="h4"
        >
          {{ $t('basket_page.title') }} ({{ basketCount }})
        </SFHeadline>
      </div>
      <SFButton variant="raw" @click="toggle">
        <template #icon>
          <IconClose class="size-5" />
        </template>
      </SFButton>
    </div>

    <ul
      class="overflow-y-auto scrollbar-hide"
      :class="{ 'max-h-56': !isMobile }"
    >
      <li
        v-if="groupedBasketItems.unavailable?.length"
        class="mt-1 flex items-center space-x-2 rounded-md bg-product-sold-out/10 px-3 py-1.5 text-sm leading-3.5 text-product-sold-out group-first:flex lg:mt-0.5"
      >
        {{ $t('basket.unavailable_products_title') }}
      </li>
      <template v-for="(group, i) in groupedBasketItems" :key="i">
        <SFHeaderBasketInfoCard
          v-for="item in group"
          :key="item.key"
          :item="item"
          @delete="deleteBasketItem(item)"
        />
      </template>
    </ul>
    <div>
      <div class="my-2 flex w-full items-center space-x-2">
        <IconInfoOutline class="size-5" />
        <span class="text-md">{{
          $t('basket.shipping_cart_not_reserved')
        }}</span>
      </div>
      <div class="flex flex-col">
        <ul>
          <li class="space-x-2 border-y border-gray-200 py-4">
            <span>{{ groupedBasketItems.available?.length }}</span>
            <span>{{ $t('global.article') }}</span>
          </li>
          <li v-if="totalDiscount > 0">
            <div class="flex justify-between border-y border-gray-200 py-4">
              <h2>{{ $t('basket_summary.discount') }}</h2>
              <span class="text-product-sale">
                {{ formatCurrency(-Math.abs(totalDiscount)) }}
              </span>
            </div>
          </li>
          <li
            class="flex justify-between space-x-2 border-y border-gray-200 py-4"
          >
            <div>
              <h2>{{ $t('basket_summary.total_amount_with_vat') }}</h2>
              <span
                v-if="isFreeShipping"
                class="text-sm font-normal text-status-success-dark"
                >{{ $t('basket_summary.delivery') }}</span
              >
            </div>
            <span v-if="basketData">
              {{ formatCurrency(basketData?.cost.withTax) }}
            </span>
          </li>
          <li
            v-if="currentShop?.coinsActive"
            class="flex items-center justify-between py-2"
          >
            <div class="flex items-center gap-2">
              <span>
                <IconSnipesCoinsoutlined
                  class="size-4 text-orange-500"
                  @click="toggleCoinsFaqSlideIn"
                />
              </span>
              <span class="text-md font-medium text-anthracite-450">{{
                $t(
                  isLoggedIn
                    ? 'global.coins.to_collect'
                    : 'global.coins.could_collect',
                )
              }}</span>
            </div>
            <button
              type="button"
              class="ml-2 flex items-center text-md font-medium text-anthracite-450 transition-colors hover:text-orange-500"
              :aria-label="$t('global.coins.faq_title')"
              @click="toggleCoinsFaqSlideIn"
            >
              {{ coinsValue }} {{ $t('global.coins.coins') }}
            </button>
          </li>
          <li class="mt-4 space-y-4">
            <SFButton
              data-testid="checkout-link"
              is-full-width
              size="xl"
              @click="goToCheckoutPage"
            >
              {{ $t('basket.go_to_checkout') }}
            </SFButton>
            <SFButton
              data-testid="cart-page"
              is-full-width
              size="xl"
              variant="secondary"
              :to="getLocalizedRoute(routeList.cart)"
            >
              {{ $t('basket.go_to_cart') }}
            </SFButton>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, useTemplateRef } from 'vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import type { BasketItem } from '@scayle/storefront-nuxt'
import { useBasket, useCurrentShop, useUser } from '#storefront/composables'
import { useFormatHelpers } from '~/composables/useFormatHelpers'
import { useBasketActions, useRouteHelpers } from '~/composables'
import { SFButton, SFHeadline } from '#storefront-ui/components'
import { getTotalStrikeThroughPrices, routeList, groupBy } from '~/utils'
import SFHeaderBasketInfoCard from '~/components/layout/headers/basket/SFHeaderBasketInfoCard.vue'
import { useSlideIn } from '#storefront-ui'
import { checkoutNavigation } from '~/composables/checkoutNavigation'
import { useBasketFlyout } from '~/composables/basket/useBasketFlyout'
import { COINS_FAQ_SLIDEIN } from '~/constants'

const { isMobile = false } = defineProps<{ isMobile?: boolean }>()

const { toggle } = useSlideIn('mobileBasketInfo')
const { items: basketItems, data: basketData, count: basketCount } = useBasket()
const { getLocalizedRoute, localizedNavigateTo } = useRouteHelpers()
const { formatCurrency } = useFormatHelpers()
const { removeItem } = useBasketActions()
const { goToCheckout } = checkoutNavigation()
const currentShop = useCurrentShop()
const { isLoggedIn, user } = useUser()
const flyout = useTemplateRef('flyout')
const { toggle: toggleCoinsFaqSlideIn } = useSlideIn(COINS_FAQ_SLIDEIN)

const { closeModalCard } = useBasketFlyout()

const { deactivate } = useFocusTrap(flyout, {
  immediate: true,
  escapeDeactivates: true,
  allowOutsideClick: true,
  onDeactivate: () => {
    closeModalCard()
  },
})

const groupedBasketItems = computed(() =>
  groupBy(basketItems.value || [], (item) => item.status),
)

const coinsValue = computed(() => {
  if (basketData?.value?.cost?.withTax != null) {
    return Math.floor(basketData.value.cost.withTax / 100)
  }
  return 0
})

const deleteBasketItem = async (item: BasketItem) => {
  await removeItem(item, { location: 'mini cart' })
}

const isFreeShipping = computed(() => {
  if (basketData?.value?.cost?.withTax) {
    return basketData?.value?.cost?.withTax / 100 >= 60
  }

  return false
})

const totalDiscount = computed(() => {
  if (!basketData?.value?.items) {
    return 0
  }
  return basketData?.value?.items?.reduce((acc, item) => {
    return acc + getTotalStrikeThroughPrices(item)
  }, 0)
})

const goToCheckoutPage = async () => {
  if (!isLoggedIn.value && !user.value) {
    document.cookie = `guest-user-active=true; Max-Age=30*24*60*60; Secure; SameSite=Strict; Path=/`

    await localizedNavigateTo(
      `${routeList.precheckout.path}?redirectUrl=${currentShop.value.path}${routeList.checkout.path}`,
    )
  }

  goToCheckout({
    tracking: 'active',
    basketItems: basketItems.value,
    originLocation: 'mini cart',
  })
}

onBeforeUnmount(() => {
  deactivate()
})
</script>
